import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { BoxOfferRulePeriod } from "@/modules/boxOffer/entities/boxOfferTypes"
import { coachOfferRuleValidationSchema, type CoachOfferRule } from "@/modules/coachOffers/types/coachOfferFormTypes"
import { cn } from "@/modules/lib/utils"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { capitalize } from "@modules/utils/stringUtils"
import { PencilIcon, PlusCircleIcon, XIcon } from "lucide-react"
import { useState } from "react"

type CoachOfferRulesInputProps = {
  rules: CoachOfferRule[]
  onChangeRules: (rules: CoachOfferRule[]) => void
} & React.ComponentProps<"div">

export function CoachOfferRulesInput({ rules, onChangeRules, className, ...props }: CoachOfferRulesInputProps) {
  const [displayNewRuleInput, setDisplayNewRuleInput] = useState(false)
  const [ruleToUpdate, setRuleToUpdate] = useState<CoachOfferRule>()

  return (
    <div className={cn("flex flex-col gap-1", className)} {...props}>
      <div className="flex flex-wrap gap-2">
        {rules.map((rule, index) =>
          ruleToUpdate?.period === rule.period ? (
            <RuleEditor
              key={index}
              rules={rules}
              onChangeRules={onChangeRules}
              ruleToUpdate={ruleToUpdate}
              onClose={() => {
                setRuleToUpdate(undefined)
              }}
            />
          ) : (
            <div
              key={index}
              onClick={() => {
                setRuleToUpdate(rule)
              }}
              className="flex h-27 max-w-48 cursor-pointer flex-row items-center gap-2 rounded-2xl border border-solid border-primary-500 bg-surface-background-secondary px-8 py-7"
            >
              <Text fontStyle="TS" className="text-center">
                {`${rule.value.toString()} ${word("offer.form.rules.sessions", { count: rule.value })} ${word(`offer.form.rules.period.per.${rule.period}`)}`}
              </Text>
              <PencilIcon size={24} />
            </div>
          ),
        )}

        {rules.length < 3 &&
          (!displayNewRuleInput ? (
            <div
              onClick={() => {
                setDisplayNewRuleInput(true)
              }}
              className="flex h-27 w-fit cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-surface-border-gray-default bg-surface-background-secondary px-8 py-7 transition-colors hover:bg-neutral-100"
            >
              <Text fontStyle="TS">{word("box.offer.form.infos.restrictedPeriod.addRule")}</Text>
              <PlusCircleIcon size={32} />
            </div>
          ) : (
            <RuleEditor
              rules={rules}
              onChangeRules={onChangeRules}
              onClose={() => {
                setDisplayNewRuleInput(false)
              }}
            />
          ))}
      </div>
    </div>
  )
}

type RuleEditorProps = {
  rules: CoachOfferRule[]
  onChangeRules: (rules: CoachOfferRule[]) => void
  onClose: () => void
  ruleToUpdate?: CoachOfferRule
} & React.ComponentProps<"form">

function RuleEditor({ rules, onChangeRules, onClose, ruleToUpdate, className, ...props }: RuleEditorProps) {
  const periodOptions = Object.values(BoxOfferRulePeriod).map((period) => ({
    value: period,
    label: capitalize(word(`offer.form.rules.period.per.${period}`)),
  }))

  const form = useAppForm({
    defaultValues: {
      value: ruleToUpdate?.value ?? 0,
      period: ruleToUpdate?.period ?? BoxOfferRulePeriod.DAY,
    } satisfies CoachOfferRule,
    validators: {
      onSubmit: coachOfferRuleValidationSchema(rules),
    },
    onSubmit: ({ value: rule }) => {
      const updatedRules = rules.some((r) => r.period === rule.period)
        ? rules.map((r) => (r.period === rule.period ? rule : r))
        : [...rules, rule]
      onChangeRules(updatedRules)
      onClose()
    },
  })

  const handleDelete = () => {
    const period = form.getFieldValue("period")
    if (rules.some((r) => r.period === period)) {
      onChangeRules(rules.filter((r) => r.period !== period))
    }
    onClose()
  }

  return (
    <form
      id="rule-editor-form"
      className={cn("mb-7 flex w-full flex-col gap-2 rounded-xl border border-borderDefault px-5 py-5", className)}
      onSubmit={(e) => {
        e.preventDefault()
        void form.handleSubmit()
      }}
      {...props}
    >
      <form.Subscribe selector={(state) => state.errors.length > 0}>
        {(hasError) => (
          <Text fontStyle="LS" className={cn("text-textOnSurface-subtle", { "text-textError": hasError })}>
            {word("box.offer.form.infos.restrictedPeriod.inputName").toUpperCase()}
          </Text>
        )}
      </form.Subscribe>

      <div className="flex w-full items-start gap-2">
        <form.AppField
          name="value"
          validators={{
            onChange: coachOfferRuleValidationSchema(rules).shape.value,
            onBlur: coachOfferRuleValidationSchema(rules).shape.value,
          }}
        >
          {(valueField) => (
            <div className="flex flex-1 flex-col gap-1">
              <InputGroup>
                <InputGroupInput
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  step={1}
                  placeholder={word("offer.form.numberOfSessions.title")}
                  value={valueField.state.value || ""}
                  onBlur={valueField.handleBlur}
                  onChange={(e) => {
                    const v = parseInt(e.target.value)
                    valueField.handleChange(Math.min(100, Math.max(1, isNaN(v) ? 0 : v)))
                  }}
                  onBeforeInput={(e) => {
                    if ("data" in e && isNaN(Number(e.data))) e.preventDefault()
                  }}
                />
                <form.AppField name="period">
                  {(periodField) => (
                    <>
                      <InputGroupAddon
                        align="inline-end"
                        className="mr-0! h-full overflow-hidden rounded-r-3xl border-l border-borderDefault p-0"
                      >
                        <periodField.Select items={periodOptions}>
                          <SelectTrigger className="h-full w-auto rounded-none border-0 bg-surface-foreground px-3 shadow-none hover:border-transparent focus-visible:border-transparent focus-visible:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {periodOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </periodField.Select>
                      </InputGroupAddon>
                    </>
                  )}
                </form.AppField>
              </InputGroup>
              <valueField.Message />
            </div>
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => rules.some((r) => r.period === state.values.period)}>
          {(exists) => (
            <div className="mt-1 flex shrink-0 gap-2">
              <Button variant="outline" size="iconMd" type="button" onClick={handleDelete}>
                {exists ? (
                  <TrashIcon color="currentColor" width={14} height={14} />
                ) : (
                  <XIcon color="currentColor" width={14} height={14} />
                )}
              </Button>
              <Button variant="primary" size="iconMd" type="submit">
                <CheckmarkIcon color="currentColor" width={14} height={14} />
              </Button>
            </div>
          )}
        </form.Subscribe>
      </div>
    </form>
  )
}
